import { z } from 'zod';
import {
  GroupedEnum,
  IAttachmentCategory,
  IControllerType,
  IFlipDirection,
  IJobType,
  IMaterialUsed,
  IProbeForZHeightCommand,
  ISoftwareUsed,
  IToolSetter,
  IUnits,
  IXYDatumPosition,
  IZZeroPosition,
} from './post.d';
import { getControllerFromFileName, getGroupedOptionsFlat } from './post.utils';
import { MODEL_FILE_EXTENSIONS } from './post.constants';
import { isValidYoutubeUrl } from '@/lib/youtube';

function createEnum<T extends string>(values: T[]): [T, ...T[]] {
  if (values.length === 0) {
    throw new Error('Enum values must have at least one item');
  }
  return values as [T, ...T[]];
}

export const modelUploadSchema = z
  .object({
    fileUploadQueue: z.array(z.any()),
    models: z.array(z.any()),
    selectedControllers: z.array(z.string()),
  })
  .superRefine((data, ctx) => {
    // Validate at least one controller is selected
    if (data.selectedControllers.length === 0) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Please select at least one controller',
        path: ['selectedControllers'],
      });
    }

    const modelsFromQueue = data.fileUploadQueue.filter(
      (file) => file.category === IAttachmentCategory.postModel
    );

    const modelsFromServer = data.models.filter(
      (model) => model.category === IAttachmentCategory.postModel
    );

    // Validate at least one model is uploaded
    if (modelsFromQueue.length === 0 && modelsFromServer.length === 0) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'At least one model is required',
        path: ['models'],
      });
    }

    // Validate at least one file per selected controller
    data.selectedControllers.forEach((controller) => {
      // Helper function to check if a model belongs to this controller
      const belongsToController = (model: {
        controller?: string;
        fileName?: string;
      }) => {
        // Direct controller match
        if (model.controller === controller) return true;

        // Case-insensitive match
        if (model.controller?.toLowerCase() === controller.toLowerCase())
          return true;

        // Categorize by filename if no controller property
        if (!model.controller && model.fileName) {
          const fileNameController = getControllerFromFileName(model.fileName);
          return fileNameController === controller;
        }

        // If model has no controller property and no filename, default to OTHER
        if (!model.controller && !model.fileName) {
          return controller === IControllerType.OTHER;
        }

        return false;
      };

      const controllerModelsFromQueue =
        modelsFromQueue.filter(belongsToController);
      const controllerModelsFromServer =
        modelsFromServer.filter(belongsToController);

      if (
        controllerModelsFromQueue.length === 0 &&
        controllerModelsFromServer.length === 0
      ) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: `At least one file must be uploaded for ${controller} controller`,
          path: ['models'],
        });
      }
    });

    modelsFromQueue.forEach((model) => {
      if (model.fileSize <= 0) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Upload a valid model (invalid file size)',
          path: ['models'],
        });
      }
    });

    modelsFromQueue.forEach((model) => {
      const modelFileExtension = model.fileName.toLowerCase().split('.').pop();
      if (
        modelFileExtension &&
        !MODEL_FILE_EXTENSIONS.includes(`.${modelFileExtension}`)
      ) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Upload a valid model (invalid file type)',
          path: ['models'],
        });
      }
    });
  });

const imageExtensions = ['jpg', 'jpeg', 'png', 'webp', 'svg'];

// Thumbnail-specific extensions (limited to common web formats)
export const thumbnailExtensions = ['.png', '.jpg', '.jpeg'];

export const allowedMediaExtensions = [
  'crv',
  'crv3d',
  'art',
  'c2d',
  'f3d',
  'ai',
  'pdf',
  'dxf',
  'stl',
  'lbn',
  'lbn2',
  'lbrn',
  'lbrn2',
  ...imageExtensions,
];

export const mediaUploadSchema = z
  .object({
    mediaFiles: z.array(z.any()),
    fileUploadQueue: z.array(z.any()),
    youtubeAttachments: z
      .array(
        z
          .string()
          .url({ message: 'Must be a valid URL' })
          .refine(isValidYoutubeUrl, {
            message: 'Must be a valid YouTube URL',
          })
      )
      .refine((urls) => new Set(urls).size === urls.length, {
        message: 'YouTube URL cant be added twice.',
      })
      .optional()
      .default([]),
  })
  .superRefine((data, ctx) => {
    const mediaFromQueue = data.fileUploadQueue.filter(
      (file) => file.category === IAttachmentCategory.postMedia
    );

    const mediaFromServer = data.mediaFiles.filter(
      (model) => model.category === IAttachmentCategory.postMedia
    );

    if (mediaFromQueue.length === 0 && mediaFromServer.length === 0) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'At least one model is required',
        path: ['mediaFiles'],
      });
    }

    mediaFromQueue.forEach((media) => {
      if (media.fileSize <= 0) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Upload a valid media (invalid file size)',
          path: ['mediaFiles'],
        });
      }
    });

    mediaFromQueue.forEach((media) => {
      const mediaFileExtension = media.fileName.toLowerCase().split('.').pop();
      if (
        mediaFileExtension &&
        !allowedMediaExtensions.includes(mediaFileExtension)
      ) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Upload a valid media (invalid file type)',
          path: ['mediaFiles'],
        });
      }
    });
  });

export const postUploadSchema = z
  .object({
    title: z.string().min(3, {
      message: 'Title must be at least 3 characters long',
    }),
    description: z.string().min(3, {
      message: 'Description must be at least 3 characters long',
    }),
    category: z.string().min(2, {
      message: 'Category must be selected',
    }),
    difficulty: z.string().min(1, {
      message: 'Difficulty must be selected',
    }),
    tags: z.array(z.string()).min(2, {
      message: 'At least two tags are required',
    }),
    fileUploadQueue: z.array(z.any()),
    thumbnail: z.any(),
  })
  .superRefine((data, ctx) => {
    const thumbnailFromQueue = data.fileUploadQueue.find(
      (file) => file.category === IAttachmentCategory.postThumbnail
    );

    const serverThumbnailPresent = data.thumbnail;

    if (!thumbnailFromQueue && !serverThumbnailPresent) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'A post thumbnail is required',
        path: ['thumbnail'],
      });
    }

    if (thumbnailFromQueue && thumbnailFromQueue.fileSize <= 0) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Upload a valid thumbnail (invalid file size)',
        path: ['thumbnail'],
      });
    }

    const thumbnailFileExtension = thumbnailFromQueue?.fileName
      .toLowerCase()
      .split('.')
      .pop();
    if (
      thumbnailFromQueue &&
      thumbnailFileExtension &&
      !imageExtensions.includes(thumbnailFileExtension)
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Upload a valid thumbnail (invalid file type)',
        path: ['thumbnail'],
      });
    }
  });

export const licenceDetailsSchema = z.object({
  licenceType: z.string({
    required_error: 'License is required',
  }),
});

export const modelToolMappingPageSchema = z
  .object({
    models: z.array(z.any()),
    modelGroups: z.array(z.any()),
  })
  .passthrough()
  .superRefine((data, ctx) => {
    // Skip validation if there are no models
    if (!data.models || data.models.length === 0) {
      return;
    }

    const modelIdList = data.models
      .filter((model) => model && model._id)
      .map((model) => model._id);

    // Collect all model IDs that are assigned to groups
    const modelIdsInGroups = new Set(
      (data.modelGroups || [])
        .filter((group) => group && Array.isArray(group.modelIds))
        .flatMap((group) => group.modelIds)
    );

    // Check if all models are assigned to at least one group
    const unassignedModels = modelIdList.filter(
      (id) => !modelIdsInGroups.has(id)
    );

    if (unassignedModels.length > 0) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['selectedModel'],
        message: 'Add tool data to all remaining gcode files',
      });
    }
  });

export const jobSetupModelSchema = z
  .object({
    softwareUsed: z
      .array(z.enum(createEnum(Object.values(ISoftwareUsed))))
      .min(1, { message: 'Please select at least one software.' }),
    materialUsed: z
      .enum(
        createEnum(
          getGroupedOptionsFlat(IMaterialUsed as unknown as GroupedEnum)
        )
      )
      .refine((val) => !!val, { message: 'Please select the material used.' }),
    jobType: z
      .enum(createEnum(Object.values(IJobType)))
      .refine((val) => !!val, { message: 'Please select a job type.' }),
    units: z
      .enum(createEnum(Object.values(IUnits)))
      .refine((val) => !!val, { message: 'Please select the units.' }),
    zZeroPosition: z
      .enum(createEnum(Object.values(IZZeroPosition)))
      .refine((val) => !!val, {
        message: 'Please select the Z zero position.',
      }),
    xyDatumPosition: z
      .enum(createEnum(Object.values(IXYDatumPosition)))
      .refine((val) => !!val, {
        message: 'Please select the XY datum position.',
      }),
    toolSetter: z
      .enum(createEnum(Object.values(IToolSetter)))
      .refine((val) => !!val, { message: 'Please select the tool setter.' }),
    probeForZHeightCommand: z
      .enum(createEnum(Object.values(IProbeForZHeightCommand)))
      .refine((val) => !!val, {
        message: 'Please select the probe for Z height command.',
      }),
    isDimensionalMaterial: z.boolean(),
    dimensionalMaterialLinks: z
      .array(
        z
          .string()
          .url({ message: 'Must be a valid URL.' })
          .refine((val) => val.startsWith('https://'), {
            message: 'URL must use https.',
          })
      )
      .default([]),
  })
  .and(
    z.discriminatedUnion('jobType', [
      z.object({
        jobType: z.literal(IJobType.ROTARY),
        jobSizeDiameter: z
          .number()
          .positive('Job diameter must be a positive number.'),
        jobSizeLength: z
          .number()
          .positive('Job length must be a positive number.'),
      }),
      z.object({
        jobType: z.literal(IJobType.SINGLE_SIDED),
        jobSizeWidth: z
          .number()
          .positive('Job width must be a positive number.'),
        jobSizeHeight: z
          .number()
          .positive('Job height must be a positive number.'),
        jobSizeThickness: z
          .number()
          .positive('Job thickness must be a positive number.'),
      }),
      z.object({
        jobType: z.literal(IJobType.DOUBLE_SIDED),
        flipDirection: z
          .enum(createEnum(Object.values(IFlipDirection)))
          .refine((val) => !!val, {
            message: 'Please select the flip direction.',
          }),
        jobSizeWidth: z
          .number()
          .positive('Job width must be a positive number.'),
        jobSizeHeight: z
          .number()
          .positive('Job height must be a positive number.'),
        jobSizeThickness: z
          .number()
          .positive('Job thickness must be a positive number.'),
      }),
      z.object({
        jobType: z.literal(IJobType.TILE),
        jobSizeWidth: z
          .number()
          .positive('Job width must be a positive number.'),
        jobSizeHeight: z
          .number()
          .positive('Job height must be a positive number.'),
        jobSizeThickness: z
          .number()
          .positive('Job thickness must be a positive number.'),
      }),
    ])
  )
  .refine(
    (data) => {
      if (data.jobType === IJobType.ROTARY) {
        return [
          IZZeroPosition.CYLINDER_SURFACE,
          IZZeroPosition.CYLINDER_AXIS,
        ].includes(data.zZeroPosition);
      }
      return [
        IZZeroPosition.MATERIAL_SURFACE,
        IZZeroPosition.MACHINE_BED,
      ].includes(data.zZeroPosition);
    },
    {
      message: 'Invalid Z Zero Position for the selected Job Type',
      path: ['zZeroPosition'],
    }
  );
